/**
 * Microphone selection, the level meter and the playback test of section 3,
 * step 4. Echo and noise suppression are *requested* here and the settings
 * actually applied are read back, because asking is not the same as getting
 * and section 8 wants the difference visible.
 */

export interface MicSettings {
  echoCancellation: boolean | null;
  noiseSuppression: boolean | null;
  autoGainControl: boolean | null;
  sampleRate: number | null;
  deviceLabel: string;
}

export const MIC_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: 1,
};

export async function openMicrophone(deviceId?: string): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: { ...MIC_CONSTRAINTS, ...(deviceId ? { deviceId: { exact: deviceId } } : {}) },
    video: false,
  });
}

export function readMicSettings(track: MediaStreamTrack): MicSettings {
  const s = track.getSettings();
  return {
    echoCancellation: s.echoCancellation ?? null,
    noiseSuppression: s.noiseSuppression ?? null,
    autoGainControl: s.autoGainControl ?? null,
    sampleRate: s.sampleRate ?? null,
    // Empty until permission is granted. The caller supplies a translated
    // fallback; this layer has no business holding user-facing words.
    deviceLabel: track.label,
  };
}

/** Labels are blank until permission is granted, so call this after getUserMedia. */
export async function listMicrophones(): Promise<MediaDeviceInfo[]> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((d) => d.kind === 'audioinput');
}

/**
 * A level meter for the pre-call check. Used only for a speaking indicator and
 * this meter — never to gate what reaches the translator, which section 8
 * forbids.
 */
export class LevelMeter {
  #context: AudioContext | null = null;
  #source: MediaStreamAudioSourceNode | null = null;
  #analyser: AnalyserNode | null = null;
  #frame: number | null = null;
  #buffer: Float32Array<ArrayBuffer> | null = null;

  start(stream: MediaStream, onLevel: (level: number) => void): void {
    this.stop();
    const context = new AudioContext();
    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(analyser);
    // Deliberately not connected to the destination: routing the microphone to
    // the speakers is how you build an echo, not how you measure one.

    this.#context = context;
    this.#source = source;
    this.#analyser = analyser;
    this.#buffer = new Float32Array(new ArrayBuffer(analyser.fftSize * 4));

    const tick = () => {
      if (!this.#analyser || !this.#buffer) return;
      this.#analyser.getFloatTimeDomainData(this.#buffer);
      let sum = 0;
      for (const sample of this.#buffer) sum += sample * sample;
      const rms = Math.sqrt(sum / this.#buffer.length);
      // Compress into a 0..1 bar that responds like an ear rather than a ruler.
      onLevel(Math.min(1, rms * 4));
      this.#frame = requestAnimationFrame(tick);
    };
    this.#frame = requestAnimationFrame(tick);
  }

  stop(): void {
    if (this.#frame !== null) cancelAnimationFrame(this.#frame);
    this.#frame = null;
    this.#source?.disconnect();
    this.#analyser?.disconnect();
    void this.#context?.close().catch(() => undefined);
    this.#context = null;
    this.#source = null;
    this.#analyser = null;
    this.#buffer = null;
  }
}

/**
 * A short tone, so a participant learns *before* the call that their output
 * device works and autoplay is unblocked. Resolves when the tone has finished.
 */
export async function playTestTone(durationMs = 600): Promise<void> {
  const context = new AudioContext();
  try {
    await context.resume();
    const osc = context.createOscillator();
    const gain = context.createGain();
    osc.frequency.value = 440;
    // A bare square edge clicks; a short ramp is the difference between a tone
    // and a pop.
    gain.gain.setValueAtTime(0, context.currentTime);
    gain.gain.linearRampToValueAtTime(0.2, context.currentTime + 0.03);
    gain.gain.linearRampToValueAtTime(0, context.currentTime + durationMs / 1000);
    osc.connect(gain).connect(context.destination);
    osc.start();
    await new Promise<void>((resolve) => setTimeout(resolve, durationMs));
    osc.stop();
  } finally {
    await context.close().catch(() => undefined);
  }
}

export function stopStream(stream: MediaStream | null): void {
  for (const track of stream?.getTracks() ?? []) track.stop();
}
